import { TmapLog, TmapLogBuilder, TmapLogType } from '@tmap-web-lib/tmap-log-client'
import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useRef } from 'react'

import { useTmapLogContext } from './useTmapLogContext'

import { usePageIdContext } from '../page-id-context'

type Props = PropsWithChildren<Partial<Pick<ContextValue, 'pageId' | 'customs'>>> & {
  serviceId?: string
}

export function TmapLogContextProvider({ serviceId, children, pageId, customs }: Props) {
  const { pageId: parentPageId, customs: parentCustoms } = useTmapLogContext()
  const pageIdContext = usePageIdContext()

  const extraCustomFields = useMemo(() => {
    return pageIdContext.extraObject?.logbox ?? {}
  }, [pageIdContext.extraObject?.logbox])

  const merged = useMemo(() => {
    const mergedPageId = pageId || parentPageId || ''
    const mergedCustoms = {
      ...parentCustoms,
      ...extraCustomFields,
      ...customs,
    }

    return {
      pageId: mergedPageId,
      customs: mergedCustoms,
    }
  }, [parentPageId, parentCustoms, pageId, customs, extraCustomFields])

  const mergedRef = useRef(merged)
  const tmapLogClient = useCallback((logType: TmapLogType) => {
    const tmapLogBuilder = TmapLog(logType)
    const { pageId, customs } = mergedRef.current

    if (serviceId) {
      tmapLogBuilder.setServiceId(serviceId)
    }
    
    if (pageId) {
      tmapLogBuilder.setPageId(pageId)
    }
    Object.keys(customs).forEach((key) => tmapLogBuilder.setCustom(key, customs[key]))
    return tmapLogBuilder
  }, [serviceId])

  const contextValue = useMemo(() => {
    return {
      ...merged,
      tmapLogClient,
    }
  }, [merged, tmapLogClient])

  useEffect(() => {
    mergedRef.current = merged
  }, [merged])

  return <TmapLogContext.Provider value={contextValue}>{children}</TmapLogContext.Provider>
}

type ContextValue = {
  pageId: string
  customs: Record<string, any>
  tmapLogClient: (logType: TmapLogType) => TmapLogBuilder
}

export const TmapLogContext = createContext<ContextValue>({
  pageId: '',
  customs: {},
  tmapLogClient: (logType: TmapLogType) => TmapLog(logType),
})
